import { useState } from 'react';

interface FortuneHistoryProps {
  history: string[];
}

export default function FortuneHistory({ history }: FortuneHistoryProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  if (history.length === 0) return null;

  return (
    <div className="w-full px-2 sm:px-0">
      <div className="bg-black/60 backdrop-blur-md border-2 sm:border-4 border-purple-400 rounded-lg overflow-hidden neon-history-glow">
        {/* Header with toggle button */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between hover:bg-purple-500/10 transition-all duration-300 cursor-pointer group"
        >
          <h3 className="text-purple-400 font-bold text-base sm:text-lg md:text-xl neon-text-history" style={{ fontFamily: '"Press Start 2P", monospace' }}>
            <span className="text-[10px] sm:text-xs">YOUR FORTUNE HISTORY</span>
          </h3>
          
          <div className="flex items-center gap-2 sm:gap-3">
            <span className="text-purple-300 text-[8px] sm:text-xs font-bold">
              {history.length} {history.length === 1 ? 'FORTUNE' : 'FORTUNES'}
            </span>
            <i className={`ri-arrow-${isExpanded ? 'up' : 'down'}-s-line text-purple-400 text-xl sm:text-2xl transition-transform duration-300 group-hover:scale-110`}></i>
          </div>
        </button>

        {/* Collapsible content */}
        <div 
          className={`transition-all duration-500 ease-in-out overflow-hidden ${
            isExpanded ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="px-4 sm:px-6 pb-4 sm:pb-6">
            <div className="space-y-2 sm:space-y-3 max-h-[400px] sm:max-h-[600px] overflow-y-auto custom-scrollbar">
              {history.map((fortune, index) => (
                <div
                  key={index}
                  className="bg-black/40 border border-2 border-cyan-400/40 rounded p-3 sm:p-4 hover:border-cyan-400 transition-all duration-300 hover:neon-card-glow group cursor-pointer"
                >
                  <div className="flex items-start gap-2 sm:gap-3">
                    <div className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 bg-gradient-to-br from-pink-500 to-purple-500 rounded-full flex items-center justify-center text-white text-[10px] sm:text-xs font-bold neon-badge">
                      {index + 1}
                    </div>
                    <p className="text-white text-xs sm:text-sm leading-relaxed group-hover:text-cyan-300 transition-colors">
                      {fortune}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .neon-history-glow {
          box-shadow: 
            0 0 20px rgba(168, 85, 247, 0.4),
            0 0 40px rgba(168, 85, 247, 0.2),
            inset 0 0 20px rgba(168, 85, 247, 0.1);
        }

        .neon-text-history {
          text-shadow: 
            0 0 10px #a855f7,
            0 0 20px #a855f7;
        }

        .neon-card-glow {
          box-shadow: 
            0 0 15px rgba(0, 255, 255, 0.4),
            0 0 30px rgba(0, 255, 255, 0.2);
        }

        .neon-badge {
          box-shadow: 
            0 0 10px rgba(255, 0, 255, 0.6),
            0 0 20px rgba(255, 0, 255, 0.3);
        }

        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }

        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.3);
          border-radius: 4px;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: linear-gradient(to bottom, #ec4899, #a855f7);
          border-radius: 4px;
          box-shadow: 0 0 10px rgba(168, 85, 247, 0.5);
        }

        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(to bottom, #f472b6, #c084fc);
        }
      `}</style>
    </div>
  );
}
